import React, { useEffect, useRef } from 'react';
import Link from './resource-link';
import { chatCard, chatSecondary } from './chat-theme';
import { HiArrowUpRight, HiOutlineExclamationCircle, HiOutlineInformationCircle } from 'react-icons/hi2';
import ClarificationCard from './clarification-card';
import ApprovalCard from './approval-card';
import { Changes, Plan, Spinner, Steps } from './activity';
import Markdown from './markdown';
import UploadCard from './upload-card';
import TestingCard, { TestingOffer } from './testing-card';
import { CustomRequestCard, DescriptionCard, DocumentsCard, GoalCard, SetupNotice, SuggestionsCard, WebsitesCard } from './onboarding-cards';
import PublishCard from './publish-card';
import { DESCRIPTION_CARD, DOCUMENTS_CARD, GOAL_CARD, REQUEST_CARD, SUGGESTIONS_CARD, WEBSITES_CARD } from '../model/onboarding';

function TypingDots() {
    return (
        <span className="inline-flex items-center gap-1 py-2" aria-label="Copilot is working">
            {[0, 150, 300].map(delay => (
                <span key={delay} className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: `${delay}ms` }} />
            ))}
        </span>
    );
}

function UserMessage({ message }) {
    return (
        <div className="flex justify-end">
            <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md border border-gray-800 bg-gray-900 px-4 py-3 text-[0.925rem] leading-relaxed text-white shadow-sm">
                {typeof message.text === 'string' ? message.text : String(message.text ?? '')}
            </div>
        </div>
    );
}

function Suggestions({ items, onPick }) {
    if (!Array.isArray(items) || !items.length || typeof onPick !== 'function') return null;
    return (
        <div className="flex flex-wrap gap-2 pt-1" role="group" aria-label="Suggested replies">
            {items.map((item, index) => (
                <button
                    key={index}
                    type="button"
                    title={item.message}
                    onClick={() => onPick(item.message)}
                    className={chatSecondary}
                >
                    {String(item.label)}
                </button>
            ))}
        </div>
    );
}

function AssistantMessage({ message, isLast, onApprove, onReject, onSuggestion, onUploaded, uploadAppId, busy, navigationVerb, setupWorking }) {
    const working = message.streaming && isLast;
    const onboarding = message.onboarding;
    // An onboarding answer is `{message, display, value}`. A playbook's card (it names a slot)
    // takes the value as data, shown as `display`; otherwise the message goes to the copilot.
    const answer = ({ message: text, display, value }) => (onboarding?.slot
        ? onSuggestion(display || text, undefined, { slot: onboarding.slot, value })
        : onSuggestion(text, display));
    return (
        <div className="space-y-2.5 text-gray-900">
            <Plan todos={Array.isArray(message.todos) ? message.todos : []} />
            <Steps steps={Array.isArray(message.steps) ? message.steps : []} running={working} />
            {message.setup && <SetupNotice setup={message.setup} working={setupWorking} />}
            {message.text ? <Markdown text={message.text} /> : working && !message.steps?.length && !message.setup && <TypingDots />}
            {/* Onboarding questions are answered once, on the latest reply; the use cases stay visible. */}
            {isLast && onboarding?.kind === GOAL_CARD && (
                <GoalCard key={message.id} request={onboarding} onSend={answer} disabled={busy || message.streaming} />
            )}
            {isLast && onboarding?.kind === REQUEST_CARD && (
                <CustomRequestCard key={message.id} request={onboarding} onSend={answer} disabled={busy || message.streaming} />
            )}
            {isLast && onboarding?.kind === WEBSITES_CARD && (
                <WebsitesCard key={message.id} request={onboarding} onSend={answer} disabled={busy || message.streaming} />
            )}
            {isLast && onboarding?.kind === DESCRIPTION_CARD && (
                <DescriptionCard key={message.id} onSend={answer} disabled={busy || message.streaming} />
            )}
            {isLast && onboarding?.kind === DOCUMENTS_CARD && (
                <DocumentsCard key={message.id} request={onboarding} appId={uploadAppId} onSend={answer} disabled={busy || message.streaming} />
            )}
            {message.publishing && <PublishCard result={message.publishing} appId={uploadAppId} />}
            {onboarding?.kind === SUGGESTIONS_CARD && (
                <SuggestionsCard key={message.id} request={onboarding} onSend={answer} disabled={busy || message.streaming} actionable={isLast} />
            )}
            <Changes changes={Array.isArray(message.changes) ? message.changes : []} />
            {(Array.isArray(message.navigations) ? message.navigations : []).map((nav, index) => (
                <Link
                    key={index}
                    to={nav.path}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-[0.8rem] font-medium text-gray-800 transition-colors hover:border-gray-300 hover:bg-gray-50"
                >
                    {navigationVerb} {String(nav.label)} <HiArrowUpRight className="h-3 w-3" />
                </Link>
            ))}
            {(Array.isArray(message.notes) ? message.notes : []).map((note, index) => (
                <p key={index} className={`${chatCard('planning')} flex items-start gap-2.5 py-3 text-[0.85rem] text-gray-700`}>
                    <HiOutlineInformationCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <span>{note}</span>
                </p>
            ))}
            {(Array.isArray(message.uploads) ? message.uploads : []).map(request => (
                <UploadCard
                    key={request.dataSourceId}
                    request={request}
                    appId={uploadAppId}
                    onUploaded={onUploaded}
                    disabled={busy}
                />
            ))}
            {message.testing && <TestingCard testing={message.testing} appId={uploadAppId} onSend={onSuggestion} disabled={busy || message.streaming} actionable={isLast} />}
            {isLast && uploadAppId && !busy && !message.streaming && !message.clarification && !onboarding && !message.publishing && !message.testing && !message.approvals?.length && message.changes?.length > 0 && (
                <TestingOffer onSend={onSuggestion} disabled={busy} />
            )}
            {message.error && (
                <p className={`${chatCard('danger')} flex items-start gap-2.5 py-3 text-[0.85rem] text-red-700 dark:text-red-300`}>
                    <HiOutlineExclamationCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{String(message.error)}</span>
                </p>
            )}
            {isLast && (
                <ApprovalCard requests={message.approvals} onApprove={onApprove} onReject={onReject} disabled={busy} />
            )}
            {/* A question with no options is answered in the composer: a card would only duplicate it. */}
            {isLast && message.clarification?.options.length > 0 && !message.approvals?.length && (
                <ClarificationCard
                    key={message.clarification.id}
                    request={message.clarification}
                    onAnswer={onSuggestion}
                    disabled={busy || message.streaming}
                    // The runtime publishes the question as the reply; don't show it twice.
                    questionShown={String(message.text || '').trim() === message.clarification.question}
                />
            )}
            {isLast && !message.clarification && !onboarding && !busy && !message.streaming && !message.approvals?.length && (
                <Suggestions items={message.suggestions} onPick={onSuggestion} />
            )}
        </div>
    );
}

export default function MessageList({ messages, running, loading, onApprove, onReject, onSuggestion, onUploaded, uploadAppId, navigationVerb = 'Opened' }) {
    const endRef = useRef(null);
    const last = messages[messages.length - 1];

    useEffect(() => {
        endRef.current?.scrollIntoView({ block: 'end' });
    }, [messages.length, last?.text, last?.steps?.length, last?.todos, last?.suggestions?.length, last?.clarification, last?.onboarding, last?.setup, running]);

    if (loading && !messages.length) {
        return <div className="flex flex-1 items-center justify-center"><Spinner className="h-5 w-5" /></div>;
    }

    const lastAssistantIndex = messages.map(m => m.role).lastIndexOf('assistant');
    return (
        <div className="space-y-7 px-3 py-6 sm:px-5">
            {messages.map((message, index) => (message.role === 'user'
                ? <UserMessage key={message.id} message={message} />
                : (
                    <AssistantMessage
                        key={message.id}
                        message={message}
                        isLast={index === lastAssistantIndex && index === messages.length - 1}
                        onApprove={onApprove}
                        onReject={onReject}
                        onSuggestion={onSuggestion}
                        onUploaded={onUploaded}
                        uploadAppId={uploadAppId}
                        busy={running}
                        navigationVerb={navigationVerb}
                        // The setup notice spins until the use cases that end its turn arrive.
                        setupWorking={Boolean(message.setup) && running
                            && !messages.slice(index).some(later => later.onboarding?.kind === SUGGESTIONS_CARD)}
                    />
                )))}
            {running && last?.role === 'user' && <TypingDots />}
            <div ref={endRef} />
        </div>
    );
}
